import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { usePostHog } from '@posthog/react';
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormKey } from '../../../enums/formKey';
import { PostHogEvent } from '../../../enums/postHogEvents';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { controlRoutes } from '../../../routes/controlRoutes';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { QuoteCoverOption } from '../QuotePage.types';

export type QuotePageActions = {
  isUpsellModalOpen: boolean;
  closeUpsellModal: () => void;
  trackOptionSelected: (option: QuoteCoverOption) => void;
  trackSelectionValidationFailed: () => void;
  continueWithSelection: (option: QuoteCoverOption) => void;
  registerOnly: () => void;
  continueToCheckout: () => void;
  goBack: (e: MouseEvent<HTMLAnchorElement>) => void;
};

/**
 * GA and PostHog tracking plus the navigation actions of a radio-style quote page.
 *
 * @param quoteData The quote being shown.
 */
export const useQuotePageActions = (quoteData: QuoteResponse): QuotePageActions => {
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const gaEventData = useGaEventData() || {};
  const posthog = usePostHog();
  const navigate = useNavigate();

  const systemCode = getSystemCode(urlParams?.rs);
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const applianceCareLabel = `${brand} Appliance Care`;
  const appSessionID = session?.id;
  const quoteId = quoteData.offer.offerReference;
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;

  const gaFields = {
    category: 'registration-pages',
    type: 'STANDARD',
    regSystemCode: systemCode,
    applianceCategory,
    applianceName,
    appSessionID,
    offerPrice,
    experimentName,
    experimentVariant,
    quoteId,
  };

  const optionLabel = (option: QuoteCoverOption) =>
    option === 'appliance-care' ? applianceCareLabel : 'Manufacturer warranty only';

  const captureQuoteEvent = (eventName: PostHogEvent) =>
    capturePostHogEvent(posthog, eventName, store.get(), { quoteId });

  useEffect(() => {
    gaPageView({ ...gaFields, pagename: 'protection-details' });
  }, []);

  const trackOptionSelected = (option: QuoteCoverOption) => {
    captureQuoteEvent(
      option === 'appliance-care'
        ? 'enhanced-protection-radio-selected'
        : 'warranty-radio-selected',
    );
    gaEvent({
      ...gaFields,
      event: 'genericGAEvent',
      eventCategory: 'AB test Quote ABCD',
      eventAction: 'radio button selected',
      eventLabel: optionLabel(option),
      pagename: 'protection-details',
    });
  };

  const trackSelectionValidationFailed = () =>
    captureQuoteEvent('protection-details-selection-validation-failed');

  const openUpsellModal = () => {
    captureQuoteEvent('protection-details-page-register-only-cta-clicked');
    gaPageView({ ...gaFields, event: 'spa-pageview', pagename: 'protection-details-popup' });
    gaEvent({
      ...gaFields,
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protectionDetails_popup_upsell',
      eventLabel: 'popup_presented',
      pagename: 'protection-details-popup',
    });
    setIsUpsellModalOpen(true);
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    captureQuoteEvent('protection-details-modal-register-only-cta-clicked');
    gaEvent({
      ...gaFields,
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
    });
    setIsUpsellModalOpen(false);
  };

  const continueToCheckout = () => {
    const fromModal = isUpsellModalOpen;

    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: fromModal },
    });
    captureQuoteEvent(
      fromModal
        ? 'protection-details-modal-monthly-upsell-cta-clicked'
        : 'protection-details-page-monthly-upsell-cta-clicked',
    );

    if (fromModal) {
      gaEvent({
        ...gaFields,
        event: 'genericGAEvent',
        eventCategory: 'appliance details form USA',
        eventAction: 'protectionDetails_popup_upsell',
        eventLabel: 'Yes, I want this protection',
        pagename: 'protection-details-popup',
      });
    }

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: fromModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      experimentName,
      experimentVariant,
      quoteId,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId,
          },
        ],
      },
    });
  };

  const continueWithSelection = (option: QuoteCoverOption) => {
    gaEvent({
      ...gaFields,
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details',
      eventLabel: 'Continue with this selection',
      pagename: 'protection-details',
      selected_flow: optionLabel(option),
    });

    if (option === 'appliance-care') {
      continueToCheckout();
    } else {
      openUpsellModal();
    }
  };

  const goBack = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    gaEvent({
      ...gaFields,
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      pagename: 'protection-details',
    });
    captureQuoteEvent('price-back-clicked');
    navigate(controlRoutes[controlRoutes.length - 1].path);
  };

  return {
    isUpsellModalOpen,
    closeUpsellModal: () => setIsUpsellModalOpen(false),
    trackOptionSelected,
    trackSelectionValidationFailed,
    continueWithSelection,
    registerOnly,
    continueToCheckout,
    goBack,
  };
};
